import { useEffect } from 'preact/hooks';
import type { RefObject } from 'preact';

/**
 * 히어로 화면에서 스크롤을 내리면 헤더를 드러낸다 (`.is-visible`).
 * 시안 index.html 의 인라인 스크립트를 옮긴 것.
 *
 * 클래스를 직접 토글하는 이유: 스크롤마다 상태를 바꾸면 페이지 전체가 리렌더된다.
 */
export function useScrollHeader(
  scrollRef: RefObject<HTMLElement | null>,
  headerRef: RefObject<HTMLElement | null>,
  showAt = 60
) {
  useEffect(() => {
    const scrollArea = scrollRef.current;
    const header = headerRef.current;
    if (!scrollArea || !header) return;

    const onScroll = () => header.classList.toggle('is-visible', scrollArea.scrollTop > showAt);

    scrollArea.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => scrollArea.removeEventListener('scroll', onScroll);
  }, [scrollRef, headerRef, showAt]);
}

/**
 * 컨테이너 안에서의 앵커 이동(#lyrics 등)을 일정한 시간으로 스크롤한다.
 *
 * 브라우저 기본 `scroll-behavior: smooth` 는 거리가 멀수록 느려져서,
 * 시안이 직접 애니메이션하던 것을 그대로 옮겼다.
 */
export function useAnchorScroll(
  scrollRef: RefObject<HTMLElement | null>,
  headerRef: RefObject<HTMLElement | null>,
  duration = 350
) {
  useEffect(() => {
    const scrollArea = scrollRef.current;
    if (!scrollArea) return;

    const easeOutQuad = (t: number) => t * (2 - t);

    const onClick = (e: Event) => {
      const anchor = (e.target as HTMLElement | null)?.closest('a[href^="#"]');
      if (!anchor) return;

      const id = anchor.getAttribute('href')?.slice(1);
      const target = id ? document.getElementById(id) : null;
      if (!target) return;

      e.preventDefault();

      const start = scrollArea.scrollTop;
      const change = target.offsetTop - (headerRef.current?.offsetHeight ?? 0) - start;
      let startTime: number | null = null;

      const step = (timestamp: number) => {
        startTime ??= timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        scrollArea.scrollTop = start + change * easeOutQuad(progress);
        if (progress < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    };

    scrollArea.addEventListener('click', onClick);
    return () => scrollArea.removeEventListener('click', onClick);
  }, [scrollRef, headerRef, duration]);
}
